import React, { useState, useEffect, useRef } from 'react';
import {
  RefreshCw, Search, Send, Trash2, MoreVertical, X, CheckCircle2, Clock, AlertCircle
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { OrderReplacement } from '../types';
import apiService from '../services/api';
import toast from 'react-hot-toast';

const STATUS_OPTIONS = ['', 'Pending', 'Approved', 'In Progress', 'Completed', 'Rejected'];

const statusBadge = (status: string) => {
  const map: Record<string, string> = {
    'Pending':     'bg-yellow-100 text-yellow-800',
    'Approved':    'bg-blue-100 text-blue-800',
    'In Progress': 'bg-orange-100 text-orange-800',
    'Completed':   'bg-emerald-100 text-emerald-800',
    'Rejected':    'bg-red-100 text-red-800',
  };
  return map[status] ?? 'bg-gray-100 text-gray-700';
};

const ReplacementsPage: React.FC = () => {
  const [items, setItems]         = useState<OrderReplacement[]>([]);
  const [loading, setLoading]     = useState(false);
  const [search, setSearch]       = useState('');
  const [statusFilter, setStatus] = useState('');
  const [showActions, setShowActions] = useState<number | null>(null);
  const [sending, setSending]     = useState<number | null>(null);

  // Close action menu on outside click
  useEffect(() => {
    if (showActions === null) return;
    const h = () => setShowActions(null);
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [showActions]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await apiService.getAllReplacements(statusFilter || undefined, search || undefined);
      setItems(res?.entity ?? res ?? []);
    } catch {
      toast.error('Failed to load replacements');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, [search, statusFilter]);

  const handleSendMessage = async (r: OrderReplacement) => {
    if (!r.id) return;
    setSending(r.id);
    try {
      const res = await apiService.sendReplacementMessage(r.id);
      if (res?.isSuccess) {
        toast.success('WhatsApp message sent');
        load();
      } else {
        toast.error(res?.message || 'Failed to send message');
      }
    } catch {
      toast.error('Failed to send message');
    } finally {
      setSending(null);
    }
  };

  const handleDelete = async (r: OrderReplacement) => {
    if (!r.id) return;
    if (!confirm(`Delete this replacement record for order ${r.orderNumber}?`)) return;
    try {
      await apiService.deleteReplacement(r.id);
      toast.success('Deleted');
      load();
    } catch {
      toast.error('Failed to delete');
    }
  };

  const fmt = (d: string) => d ? new Date(d).toLocaleDateString('en-IN') : '—';
  const fmtCurrency = (n: number) => `₹${Math.round(n).toLocaleString('en-IN')}`;

  const totalCost = items.reduce((s, r) => s + (r.replacementCost || 0), 0);
  const pending   = items.filter(r => r.replacementStatus === 'Pending').length;
  const completed = items.filter(r => r.replacementStatus === 'Completed').length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-violet-500 to-violet-600 rounded-lg flex items-center justify-center">
            <RefreshCw className="h-5 w-5 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Replacements</h1>
            <p className="text-sm text-gray-500">Track device replacement requests</p>
          </div>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-3 gap-3">
        <div className="bg-violet-50 border border-violet-200 rounded-xl px-4 py-3">
          <p className="text-xs font-medium text-violet-600 mb-0.5">Total Cost</p>
          <p className="text-xl font-bold text-violet-800">{fmtCurrency(totalCost)}</p>
          <p className="text-xs text-violet-500">{items.length} records</p>
        </div>
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl px-4 py-3">
          <p className="text-xs font-medium text-yellow-600 mb-0.5">Pending</p>
          <p className="text-xl font-bold text-yellow-800">{pending}</p>
          <p className="text-xs text-yellow-500">awaiting action</p>
        </div>
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl px-4 py-3">
          <p className="text-xs font-medium text-emerald-600 mb-0.5">Completed</p>
          <p className="text-xl font-bold text-emerald-800">{completed}</p>
          <p className="text-xs text-emerald-500">done</p>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 shadow-sm flex flex-wrap items-end gap-4">
        <div className="flex-1 min-w-[200px]">
          <label className="block text-xs font-medium text-gray-600 mb-1">Search</label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Order #, customer name or mobile..."
              className="w-full pl-9 pr-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
            />
            {search && (
              <button onClick={() => setSearch('')} className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                <X className="h-4 w-4" />
              </button>
            )}
          </div>
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-600 mb-1">Status</label>
          <select
            value={statusFilter}
            onChange={e => setStatus(e.target.value)}
            className="px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500"
          >
            {STATUS_OPTIONS.map(s => (
              <option key={s} value={s}>{s || 'All Status'}</option>
            ))}
          </select>
        </div>
        <div className="text-xs text-gray-500 self-end pb-2">{items.length} records</div>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-600" />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-16">
            <RefreshCw className="mx-auto h-10 w-10 text-gray-300 mb-3" />
            <p className="text-gray-500 font-medium">No replacements found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order</th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Customer</th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Technician</th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Cost</th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Msg</th>
                  <th className="px-5 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Notes</th>
                  <th className="px-5 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {items.map(r => (
                  <tr key={r.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-5 py-3 whitespace-nowrap">
                      <Link
                        to={`/orders/edit/${r.orderId}`}
                        className="text-sm font-medium text-violet-700 hover:underline"
                      >
                        {r.orderNumber || `#${r.orderId}`}
                      </Link>
                    </td>
                    <td className="px-5 py-3 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900">{r.customerName || '—'}</div>
                      <div className="text-xs text-gray-500">{r.customerMobile || ''}</div>
                    </td>
                    <td className="px-5 py-3 whitespace-nowrap text-sm text-gray-700">
                      {r.technicianName || <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-5 py-3 whitespace-nowrap text-sm font-semibold text-gray-900">
                      {fmtCurrency(r.replacementCost)}
                    </td>
                    <td className="px-5 py-3 whitespace-nowrap">
                      <span className={`inline-flex px-2 py-0.5 text-xs font-semibold rounded-full ${statusBadge(r.replacementStatus)}`}>
                        {r.replacementStatus}
                      </span>
                    </td>
                    <td className="px-5 py-3 whitespace-nowrap text-sm text-gray-500">
                      {fmt(r.replacementDate)}
                    </td>
                    <td className="px-5 py-3 whitespace-nowrap">
                      {r.messageSent
                        ? <span title="Message sent"><CheckCircle2 className="h-4 w-4 text-emerald-500" /></span>
                        : <span title="Not sent"><Clock className="h-4 w-4 text-gray-300" /></span>}
                    </td>
                    <td className="px-5 py-3 max-w-[180px]">
                      <p className="text-xs text-gray-500 truncate" title={r.notes ?? ''}>{r.notes || '—'}</p>
                    </td>
                    <td className="px-5 py-3 whitespace-nowrap text-right">
                      <div className="relative">
                        <button
                          onClick={(e) => { e.stopPropagation(); setShowActions(showActions === r.id ? null : r.id!); }}
                          className="text-gray-400 hover:text-gray-600"
                        >
                          <MoreVertical className="h-4 w-4" />
                        </button>
                        {showActions === r.id && (
                          <div onMouseDown={e => e.stopPropagation()} className="absolute right-0 mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-10 py-1">
                            <button
                              onClick={() => { handleSendMessage(r); setShowActions(null); }}
                              disabled={sending === r.id}
                              className="flex items-center w-full px-4 py-2 text-sm text-blue-600 hover:bg-blue-50 disabled:opacity-50"
                            >
                              <Send className="h-4 w-4 mr-2" />
                              {sending === r.id ? 'Sending…' : 'Send WhatsApp'}
                            </button>
                            <Link
                              to={`/orders/edit/${r.orderId}`}
                              className="flex items-center px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
                              onClick={() => setShowActions(null)}
                            >
                              <AlertCircle className="h-4 w-4 mr-2" />
                              View Order
                            </Link>
                            <button
                              onClick={() => { handleDelete(r); setShowActions(null); }}
                              className="flex items-center w-full px-4 py-2 text-sm text-red-600 hover:bg-red-50"
                            >
                              <Trash2 className="h-4 w-4 mr-2" />
                              Delete
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReplacementsPage;
